import React, { Suspense, Component, ErrorInfo } from 'react';
import { RouterProvider } from 'react-router-dom';
import './App.scss';

import theme from './theme';
import { Provider } from 'jotai';
import { store } from './services/atom.service';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ImpersonationProvider } from './hooks/useImpersonation';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { router } from './router';
import { Loading } from './components/Loading';

// ---------------------------------------------------------------------------
// Top-level error boundary — catches chunk-load errors that slip past the
// unhandledrejection handler (e.g. errors thrown during render).
// ---------------------------------------------------------------------------
class AppErrorBoundary extends Component<
    { children: React.ReactNode },
    { crashed: boolean; isChunkError: boolean }
> {
    state = { crashed: false, isChunkError: false };

    static getDerivedStateFromError(error: Error) {
        const msg = error?.message ?? '';
        const isChunkError =
            msg.includes('Failed to fetch dynamically imported module') ||
            msg.includes('error loading dynamically imported module') ||
            msg.includes('Importing a module script failed');
        return { crashed: true, isChunkError };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error('[AppErrorBoundary]', error, info);
    }

    handleReload = () => {
        sessionStorage.removeItem('chunk_reload_attempted');
        window.location.reload();
    };

    render() {
        if (!this.state.crashed) return this.props.children;

        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100vh"
                gap={2}
                p={3}
                textAlign="center"
            >
                {this.state.isChunkError ? (
                    <>
                        <Typography variant="h6">A new version is available</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Please refresh the page to load the latest version.
                        </Typography>
                        <Button variant="contained" onClick={this.handleReload}>
                            Refresh now
                        </Button>
                    </>
                ) : (
                    <>
                        <Typography variant="h6">Something went wrong</Typography>
                        <Button variant="outlined" onClick={this.handleReload}>
                            Reload page
                        </Button>
                    </>
                )}
            </Box>
        );
    }
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 10 * 60 * 1000 // 10 minutes
        }
    }
});

export default function App() {

    return (
        <AppErrorBoundary>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <ThemeProvider theme={theme}>
                    <QueryClientProvider client={queryClient}>
                        <Provider store={store}>
                            <ImpersonationProvider>
                                <Suspense fallback={<Loading />}>
                                    <RouterProvider router={router} />
                                </Suspense>
                            </ImpersonationProvider>
                        </Provider>
                    </QueryClientProvider>
                </ThemeProvider>
            </LocalizationProvider>
        </AppErrorBoundary>
    );
}
